import { apiAiChatCompletions } from '../../api/ai';
import { debug } from '../debug';
import { sanitizeInputByType } from '../formatHelpers';
import { extractAssistantTextFromOpenRouter } from './aiResponseHelpers';

export const INTENT_KEYS = ['ask', 'edit', 'summarize', 'compare', 'brainstorm', 'lookup'];
export const FALLBACK_INTENT_THRESHOLD = 0.68;
const MAX_DEFAULT_PRIMARY_KEYWORDS = 8;
const MAX_DEFAULT_SECONDARY_KEYWORDS = 12;
const MAX_DEFAULT_DOC_HINTS = 8;
const DEFAULT_LONG_PROMPT_MIN_SEARCHABLE_KEYWORDS = 4;
const SHORT_SIGNAL_KEYWORDS = new Set([
  'ai', 'api', 'db', 'ml', 'nlp', 'qa', 'r2', 'sql', 'ui', 'ux', 'llm'
]);
const HEURISTIC_KEYWORD_STOPWORDS = new Set([
  'about', 'can', 'could', 'describe', 'each', 'explain', 'for', 'from', 'give', 'how', 'list',
  'max', 'please', 'show', 'tell', 'that', 'the', 'their', 'them', 'these', 'this', 'those',
  'what', 'when', 'where', 'which', 'who', 'why', 'with', 'works', 'work', 'bullet', 'bullets'
]);
const DEFAULT_CONTRACT_META = {
  modelAttempted: false,
  decisionCode: 'intent_contract_heuristic_default'
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const normalizeIntentKey = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  return INTENT_KEYS.includes(normalized) ? normalized : 'ask';
};

const normalizeSearchText = (value = '') => {
  return String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeKeywordToken = (value = '') => String(value || '').trim().toLowerCase();

const isShortSignalKeyword = (value = '') => {
  const token = normalizeKeywordToken(value);
  return token.length >= 2 && token.length <= 4 && SHORT_SIGNAL_KEYWORDS.has(token);
};

const isHeuristicKeywordCandidate = (value = '', { minLength = 4 } = {}) => {
  const token = normalizeKeywordToken(value);
  if (!token) return false;
  if (!/[a-z]/i.test(token)) return false;
  if (HEURISTIC_KEYWORD_STOPWORDS.has(token)) return false;
  if (token.length >= minLength) return true;
  return isShortSignalKeyword(token);
};

export const extractSearchableKeywords = (value = '', { minLength = 4, maxItems = 24 } = {}) => {
  const limit = Math.max(1, Number(maxItems || 24));
  return Array.from(new Set(
    normalizeSearchText(value)
      .split(/\s+/)
      .map((token) => token.replace(/[^a-zA-Z0-9&._-]/g, '').trim().toLowerCase())
      .filter(Boolean)
      .filter((token) => isHeuristicKeywordCandidate(token, { minLength }))
  )).slice(0, limit);
};

const scoreIntentRules = ({ text = '', mode = 'Ask' } = {}) => {
  const normalizedText = normalizeSearchText(text).toLowerCase();
  const normalizedMode = String(mode || '').trim().toLowerCase();
  const scores = {
    ask: 0,
    edit: 0,
    summarize: 0,
    compare: 0,
    brainstorm: 0,
    lookup: 0
  };

  const add = (key, points, pattern) => {
    if (pattern.test(normalizedText)) {
      scores[key] += points;
    }
  };

  add('edit', 3, /\b(rewrite|reword|rephrase|edit|fix|polish|improve|tighten|shorten|expand|correct grammar|proofread)\b/);
  add('summarize', 4, /\b(summarize|summary|tl;dr|tldr|recap|briefly summarize|key points)\b/);
  add('compare', 4, /\b(compare|comparison|vs\.?|versus|difference|differences|pros and cons|trade[- ]?offs?)\b/);
  add('brainstorm', 4, /\b(brainstorm|ideas|idea list|alternatives|options|creative directions|generate ideas)\b/);
  add('lookup', 3, /\b(look up|lookup|find|search|where is|which doc|show me docs?|reference|locate)\b/);
  add('ask', 2, /\b(what|why|how|when|who|which|explain|help|question)\b/);

  if (/\?$/.test(normalizedText)) {
    scores.ask += 1;
  }
  if (/\bvs\b/.test(normalizedText)) {
    scores.compare += 2;
  }

  if (normalizedMode === 'edit' || normalizedMode === 'write') {
    scores.edit += 2;
  }
  if (normalizedMode === 'ask' || normalizedMode === 'explore') {
    scores.ask += 1;
  }

  return scores;
};

const extractTopIntent = (scores = {}) => {
  const ranked = Object.entries(scores)
    .sort((left, right) => right[1] - left[1]);

  const [top = ['ask', 0], second = ['ask', 0]] = ranked;
  const topKey = normalizeIntentKey(top[0]);
  const topScore = Number(top[1] || 0);
  const secondScore = Number(second[1] || 0);
  const margin = topScore - secondScore;
  const base = 0.42 + (topScore * 0.09) + (margin * 0.07);
  const confidence = clamp(base, 0.35, 0.98);

  if (topScore <= 0) {
    return {
      key: 'ask',
      confidence: 0.4
    };
  }

  return {
    key: topKey,
    confidence: margin <= 0 ? clamp(confidence - 0.12, 0.35, 0.98) : confidence
  };
};

const parseModelIntentPayload = (payloadText = '') => {
  const raw = String(payloadText || '').trim();
  if (!raw) return null;

  const fullJson = (() => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();

  const parsed = fullJson || (() => {
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return null;
    try {
      return JSON.parse(match[0]);
    } catch {
      return null;
    }
  })();

  if (!parsed || typeof parsed !== 'object') return null;

  const key = normalizeIntentKey(parsed.intent || parsed.key);
  if (!INTENT_KEYS.includes(key)) return null;

  const confidenceValue = Number(parsed.confidence);
  return {
    key,
    confidence: Number.isFinite(confidenceValue)
      ? clamp(confidenceValue, 0, 1)
      : null
  };
};

const normalizeKeywordList = (values = [], maxItems = MAX_DEFAULT_PRIMARY_KEYWORDS) => {
  const list = Array.isArray(values) ? values : [];
  const max = Math.max(1, Number(maxItems || MAX_DEFAULT_PRIMARY_KEYWORDS));
  const seen = new Set();
  const normalized = [];

  for (const raw of list) {
    const token = String(raw || '').trim();
    if (!token) continue;
    const canonical = token.toLowerCase();
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    normalized.push(token);
    if (normalized.length >= max) break;
  }

  return normalized;
};

const normalizeDocHints = (values = [], maxItems = MAX_DEFAULT_DOC_HINTS) => {
  const list = Array.isArray(values) ? values : [];
  const max = Math.max(1, Number(maxItems || MAX_DEFAULT_DOC_HINTS));
  const seen = new Set();
  const normalized = [];

  for (const entry of list) {
    const normalizedEntry = entry && typeof entry === 'object'
      ? {
        docId: Number.isFinite(Number(entry.docId || entry.doc_id))
          ? Math.max(1, Math.round(Number(entry.docId || entry.doc_id)))
          : null,
        label: String(entry.label || entry.doc_name || entry.name || '').trim() || null,
        confidence: Number.isFinite(Number(entry.confidence))
          ? Math.max(0, Math.min(1, Number(entry.confidence)))
          : null,
        reason: String(entry.reason || entry.reasonCode || '').trim() || null
      }
      : {
        docId: null,
        label: String(entry || '').trim() || null,
        confidence: null,
        reason: null
      };

    if (!normalizedEntry.label && !normalizedEntry.docId) continue;
    const dedupeKey = `${normalizedEntry.docId || ''}::${String(normalizedEntry.label || '').toLowerCase()}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    normalized.push(normalizedEntry);
    if (normalized.length >= max) break;
  }

  return normalized;
};

const normalizeBudgetTier = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'small' || normalized === 'large') return normalized;
  return 'medium';
};

const hasUsefulIntentContractContent = (contract = null) => {
  if (!contract || typeof contract !== 'object') return false;
  const hasSummary = Boolean(String(contract.goalSummary || '').trim());
  const hasPrimary = Array.isArray(contract.primaryKeywords) && contract.primaryKeywords.length > 0;
  const hasSecondary = Array.isArray(contract.secondaryKeywords) && contract.secondaryKeywords.length > 0;
  const hasRewrites = Array.isArray(contract.queryRewrites) && contract.queryRewrites.length > 0;
  const hasDocHints = Array.isArray(contract.docHints) && contract.docHints.length > 0;
  const hasBudgetHint = Boolean(contract.budgetHint && typeof contract.budgetHint === 'object' && (
    Number.isFinite(Number(contract.budgetHint.targetContextChars))
    || Number.isFinite(Number(contract.budgetHint.targetContextItems))
    || String(contract.budgetHint.tier || '').trim().length > 0
  ));
  return hasSummary || hasPrimary || hasSecondary || hasRewrites || hasDocHints || hasBudgetHint;
};

const buildHeuristicIntentContract = ({
  userText = '',
  heuristicIntent = null,
  maxPrimaryKeywords = MAX_DEFAULT_PRIMARY_KEYWORDS,
  maxSecondaryKeywords = MAX_DEFAULT_SECONDARY_KEYWORDS,
  maxDocHints = MAX_DEFAULT_DOC_HINTS,
  contextBundle = null
} = {}) => {
  const normalizedText = normalizeSearchText(userText);
  const tokenCandidates = normalizedText
    .split(/\s+/)
    .map(token => token.replace(/[^a-zA-Z0-9&._-]/g, '').trim())
    .filter(Boolean);

  const keywordCandidates = tokenCandidates.filter(token => isHeuristicKeywordCandidate(token, { minLength: 4 }));
  const primaryKeywordCandidates = keywordCandidates.filter(token => isHeuristicKeywordCandidate(token, { minLength: 5 }));

  const primaryKeywords = normalizeKeywordList(
    primaryKeywordCandidates.slice(0, Math.max(1, maxPrimaryKeywords)),
    maxPrimaryKeywords
  );
  const secondaryKeywords = normalizeKeywordList(
    keywordCandidates,
    maxSecondaryKeywords
  );
  const concisePrimaryRewrite = normalizeKeywordList(primaryKeywords, 6).join(' ').trim();
  const signalDenseRewrite = normalizeKeywordList([
    ...primaryKeywords.filter(isShortSignalKeyword),
    ...primaryKeywords.filter(token => !isShortSignalKeyword(token))
  ], 4).join(' ').trim();
  const linkedDocHints = normalizeDocHints(
    contextBundle?.linkedDocHints
    || contextBundle?.linkedDocSummaries
    || [],
    maxDocHints
  );

  return {
    source: 'heuristic',
    goalSummary: String(normalizedText || '').trim() || null,
    primaryKeywords,
    secondaryKeywords,
    queryRewrites: normalizeKeywordList([
      normalizedText,
      `${String(heuristicIntent?.key || 'ask').trim()} ${normalizedText}`.trim(),
      concisePrimaryRewrite,
      signalDenseRewrite
    ], 4),
    docHints: linkedDocHints,
    budgetHint: {
      tier: normalizeBudgetTier(primaryKeywords.length >= 6 ? 'large' : (primaryKeywords.length <= 2 ? 'small' : 'medium')),
      targetContextChars: primaryKeywords.length >= 6 ? 14000 : (primaryKeywords.length <= 2 ? 5000 : 9000),
      targetContextItems: primaryKeywords.length >= 6 ? 18 : (primaryKeywords.length <= 2 ? 8 : 12)
    }
  };
};

const parseModelIntentContractPayload = ({
  payloadText = '',
  fallbackIntent = null,
  maxPrimaryKeywords = MAX_DEFAULT_PRIMARY_KEYWORDS,
  maxSecondaryKeywords = MAX_DEFAULT_SECONDARY_KEYWORDS,
  maxDocHints = MAX_DEFAULT_DOC_HINTS
} = {}) => {
  const raw = String(payloadText || '').trim();
  if (!raw) return null;

  const parsed = (() => {
    try {
      return JSON.parse(raw);
    } catch {
      const match = raw.match(/\{[\s\S]*\}/);
      if (!match) return null;
      try {
        return JSON.parse(match[0]);
      } catch {
        return null;
      }
    }
  })();

  if (!parsed || typeof parsed !== 'object') return null;

  const intentKey = normalizeIntentKey(parsed.intent || parsed.intentKey || fallbackIntent?.key || 'ask');
  const confidenceValue = Number(parsed.confidence);
  const confidence = Number.isFinite(confidenceValue)
    ? clamp(confidenceValue, 0, 1)
    : Number(fallbackIntent?.confidence || 0);

  const budgetHintRaw = parsed.budget_hint && typeof parsed.budget_hint === 'object'
    ? parsed.budget_hint
    : (parsed.budgetHint && typeof parsed.budgetHint === 'object' ? parsed.budgetHint : {});

  const contract = {
    source: 'model',
    goalSummary: String(parsed.goal_summary || parsed.goalSummary || parsed.objective || '').trim() || null,
    primaryKeywords: normalizeKeywordList(
      parsed.primary_keywords || parsed.primaryKeywords || parsed.keywords || [],
      maxPrimaryKeywords
    ),
    secondaryKeywords: normalizeKeywordList(
      parsed.secondary_keywords || parsed.secondaryKeywords || [],
      maxSecondaryKeywords
    ),
    queryRewrites: normalizeKeywordList(
      parsed.query_rewrites || parsed.queryRewrites || parsed.query_expansions || parsed.queryExpansions || [],
      6
    ),
    docHints: normalizeDocHints(parsed.doc_hints || parsed.docHints || parsed.doc_candidates || [], maxDocHints),
    budgetHint: {
      tier: normalizeBudgetTier(budgetHintRaw.tier || budgetHintRaw.level || 'medium'),
      targetContextChars: Number.isFinite(Number(budgetHintRaw.target_context_chars || budgetHintRaw.targetContextChars))
        ? Math.max(1200, Math.round(Number(budgetHintRaw.target_context_chars || budgetHintRaw.targetContextChars)))
        : null,
      targetContextItems: Number.isFinite(Number(budgetHintRaw.target_context_items || budgetHintRaw.targetContextItems))
        ? Math.max(3, Math.round(Number(budgetHintRaw.target_context_items || budgetHintRaw.targetContextItems)))
        : null
    }
  };

  if (!hasUsefulIntentContractContent(contract)) {
    return null;
  }

  return {
    key: intentKey,
    confidence,
    contract
  };
};

export const classifyChatIntentHeuristics = ({ userText = '', mode = 'Ask' } = {}) => {
  const sanitizedUserText = sanitizeInputByType(userText, 'chat');
  const scores = scoreIntentRules({ text: sanitizedUserText, mode });
  const top = extractTopIntent(scores);

  return {
    key: top.key,
    confidence: top.confidence,
    source: 'heuristic'
  };
};

export const classifyChatIntentWithModelFallback = async ({
  userText = '',
  mode = 'Explore',
  heuristicIntent = null,
  enabled = false,
  model,
  threshold = FALLBACK_INTENT_THRESHOLD,
  signal,
  onModelAttempt,
  classifyIntentModel = apiAiChatCompletions
} = {}) => {
  const sanitizedUserText = sanitizeInputByType(userText, 'chat');
  const localHeuristic = heuristicIntent || classifyChatIntentHeuristics({ userText: sanitizedUserText, mode });
  const normalizedConfidence = Number(localHeuristic?.confidence || 0);

  if (!enabled || normalizedConfidence >= threshold || typeof classifyIntentModel !== 'function') {
    return localHeuristic;
  }

  const promptText = sanitizeInputByType(sanitizedUserText, 'chat').trim();
  if (!promptText) return localHeuristic;

  const messages = [
    {
      role: 'system',
      content: 'Classify user chat intent. Return JSON only with keys intent and confidence. Allowed intents: ask, edit, summarize, compare, brainstorm, lookup.'
    },
    {
      role: 'user',
      content: JSON.stringify({
        mode: String(mode || 'Explore'),
        text: promptText,
        instructions: 'Return strict JSON like {"intent":"ask","confidence":0.84}.'
      })
    }
  ];

  try {
    const attemptStart = Date.now();
    const payload = await classifyIntentModel({
      model,
      messages,
      temperature: 0,
      max_tokens: 120,
      stream: false,
      signal
    });
    if (typeof onModelAttempt === 'function') {
      const usage = payload?.usage && typeof payload.usage === 'object'
        ? {
            promptTokens: Number(payload.usage.prompt_tokens ?? payload.usage.promptTokens ?? 0) || 0,
            completionTokens: Number(payload.usage.completion_tokens ?? payload.usage.completionTokens ?? 0) || 0,
            totalTokens: Number(payload.usage.total_tokens ?? payload.usage.totalTokens ?? 0) || 0
          }
        : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
      onModelAttempt({
        stageName: 'intent',
        callKind: 'intent',
        modelId: String(model || '').trim() || null,
        responseModel: String(payload?.model || model || '').trim() || null,
        providerRequestId: String(payload?.id || '').trim() || null,
        usage,
        status: 'success',
        durationMs: Date.now() - attemptStart,
        errorCode: null,
        errorMessage: null
      });
    }
    const parsed = parseModelIntentPayload(extractAssistantTextFromOpenRouter(payload));
    if (!parsed) return localHeuristic;

    return {
      key: normalizeIntentKey(parsed.key),
      confidence: parsed.confidence === null ? localHeuristic.confidence : parsed.confidence,
      source: 'model-fallback'
    };
  } catch (error) {
    if (typeof onModelAttempt === 'function') {
      const errorMessage = String(error?.message || 'intent fallback classification failed');
      const timeoutLike = /timeout|timed out|cancelled before completion/i.test(errorMessage);
      onModelAttempt({
        stageName: 'intent',
        callKind: 'intent',
        modelId: String(model || '').trim() || null,
        responseModel: null,
        providerRequestId: null,
        usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
        status: timeoutLike ? 'timeout' : 'failed',
        durationMs: 0,
        errorCode: timeoutLike ? 'REQUEST_TIMEOUT' : (String(error?.code || '').trim() || null),
        errorMessage
      });
    }
    debug('classifyChatIntentWithModelFallback', { type: 'warn' }, error?.message || 'intent fallback classification failed');
    return localHeuristic;
  }
};

export const classifyChatIntentAndContractWithModelFallback = async ({
  userText = '',
  mode = 'Explore',
  heuristicIntent = null,
  enabled = false,
  alwaysOn = false,
  forceOnLongPrompts = false,
  longPromptMinSearchableKeywords = DEFAULT_LONG_PROMPT_MIN_SEARCHABLE_KEYWORDS,
  model,
  threshold = FALLBACK_INTENT_THRESHOLD,
  signal,
  onModelAttempt,
  contextBundle = null,
  maxPrimaryKeywords = MAX_DEFAULT_PRIMARY_KEYWORDS,
  maxSecondaryKeywords = MAX_DEFAULT_SECONDARY_KEYWORDS,
  maxDocHints = MAX_DEFAULT_DOC_HINTS,
  classifyIntentModel = apiAiChatCompletions
} = {}) => {
  const sanitizedUserText = sanitizeInputByType(userText, 'chat');
  const localHeuristic = heuristicIntent || classifyChatIntentHeuristics({ userText: sanitizedUserText, mode });
  const heuristicContract = buildHeuristicIntentContract({
    userText: sanitizedUserText,
    heuristicIntent: localHeuristic,
    maxPrimaryKeywords,
    maxSecondaryKeywords,
    maxDocHints,
    contextBundle
  });
  const normalizedConfidence = Number(localHeuristic?.confidence || 0);
  const searchableKeywords = extractSearchableKeywords(sanitizedUserText, { minLength: 4, maxItems: 32 });
  const longPromptTriggered = Boolean(forceOnLongPrompts)
    && searchableKeywords.length >= Math.max(1, Number(longPromptMinSearchableKeywords || DEFAULT_LONG_PROMPT_MIN_SEARCHABLE_KEYWORDS));
  const shouldCallModel = Boolean(enabled)
    && typeof classifyIntentModel === 'function'
    && (Boolean(alwaysOn) || normalizedConfidence < threshold || longPromptTriggered);

  if (!shouldCallModel) {
    const decisionCode = !enabled
      ? 'intent_contract_model_disabled'
      : (typeof classifyIntentModel !== 'function'
        ? 'intent_contract_model_unavailable'
        : 'intent_contract_heuristic_confident');
    return {
      ...localHeuristic,
      contract: heuristicContract,
      contractMeta: {
        ...DEFAULT_CONTRACT_META,
        decisionCode,
        longPromptTriggered
      }
    };
  }

  const promptText = sanitizeInputByType(sanitizedUserText, 'chat').trim();
  if (!promptText) {
    return {
      ...localHeuristic,
      contract: heuristicContract,
      contractMeta: {
        ...DEFAULT_CONTRACT_META,
        decisionCode: 'intent_contract_prompt_empty'
      }
    };
  }

  const messages = [
    {
      role: 'system',
      content: [
        'Return STRICT JSON only.',
        'Task: classify intent and produce retrieval/goal contract.',
        'Allowed intents: ask, edit, summarize, compare, brainstorm, lookup.',
        'Prioritize the latest user message over historical context.',
        'If the user message is a correction (for example starts with "I mean", "actually", "instead", "to clarify"), treat it as an override and exclude stale entities not present in the latest message.',
        'Required JSON shape:',
        '{"intent":"ask","confidence":0.84,"goal_summary":"...","primary_keywords":["..."],"secondary_keywords":["..."],"query_rewrites":["..."],"doc_hints":[{"label":"...","doc_id":123,"confidence":0.0,"reason":"..."}],"budget_hint":{"tier":"small|medium|large","target_context_chars":8000,"target_context_items":12}}'
      ].join('\n')
    },
    {
      role: 'user',
      content: JSON.stringify({
        mode: String(mode || 'Explore'),
        text: promptText,
        heuristic_intent: {
          key: localHeuristic?.key || 'ask',
          confidence: Number(localHeuristic?.confidence || 0)
        },
        context_bundle: contextBundle && typeof contextBundle === 'object' ? contextBundle : null,
        instructions: 'Return strict JSON only. No markdown. No prose.'
      })
    }
  ];

  try {
    const attemptStart = Date.now();
    const payload = await classifyIntentModel({
      model,
      messages,
      temperature: 0,
      max_tokens: 420,
      stream: false,
      signal
    });

    if (typeof onModelAttempt === 'function') {
      const usage = payload?.usage && typeof payload.usage === 'object'
        ? {
            promptTokens: Number(payload.usage.prompt_tokens ?? payload.usage.promptTokens ?? 0) || 0,
            completionTokens: Number(payload.usage.completion_tokens ?? payload.usage.completionTokens ?? 0) || 0,
            totalTokens: Number(payload.usage.total_tokens ?? payload.usage.totalTokens ?? 0) || 0
          }
        : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
      onModelAttempt({
        stageName: 'intent',
        callKind: 'intent_contract',
        modelId: String(model || '').trim() || null,
        responseModel: String(payload?.model || model || '').trim() || null,
        providerRequestId: String(payload?.id || '').trim() || null,
        usage,
        status: 'success',
        durationMs: Date.now() - attemptStart,
        errorCode: null,
        errorMessage: null
      });
    }

    const parsed = parseModelIntentContractPayload({
      payloadText: extractAssistantTextFromOpenRouter(payload),
      fallbackIntent: localHeuristic,
      maxPrimaryKeywords,
      maxSecondaryKeywords,
      maxDocHints
    });

    if (!parsed) {
      return {
        ...localHeuristic,
        contract: heuristicContract,
        contractMeta: {
          modelAttempted: true,
          decisionCode: 'intent_contract_model_unusable_payload'
        }
      };
    }

    return {
      key: normalizeIntentKey(parsed.key),
      confidence: Number.isFinite(Number(parsed.confidence))
        ? Number(parsed.confidence)
        : Number(localHeuristic?.confidence || 0),
      source: 'model-fallback',
      contract: {
        ...heuristicContract,
        ...(parsed.contract && typeof parsed.contract === 'object' ? parsed.contract : {})
      },
      contractMeta: {
        modelAttempted: true,
        decisionCode: 'intent_contract_model_applied'
      }
    };
  } catch (error) {
    if (typeof onModelAttempt === 'function') {
      const errorMessage = String(error?.message || 'intent contract classification failed');
      const timeoutLike = /timeout|timed out|cancelled before completion/i.test(errorMessage);
      onModelAttempt({
        stageName: 'intent',
        callKind: 'intent_contract',
        modelId: String(model || '').trim() || null,
        responseModel: null,
        providerRequestId: null,
        usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
        status: timeoutLike ? 'timeout' : 'failed',
        durationMs: 0,
        errorCode: timeoutLike ? 'REQUEST_TIMEOUT' : (String(error?.code || '').trim() || null),
        errorMessage
      });
    }
    debug('classifyChatIntentAndContractWithModelFallback', { type: 'warn' }, error?.message || 'intent contract classification failed');
    return {
      ...localHeuristic,
      contract: heuristicContract,
      contractMeta: {
        modelAttempted: true,
        decisionCode: 'intent_contract_model_error'
      }
    };
  }
};
